import React, { useMemo } from 'react'
import SlideBase from './SlideBase'
import WrappedImageBackground from '../WrappedImageBackground'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferPagePackageInclusionsList from 'components/OfferPage/OfferPagePackageInclusionsList'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import InteractiveZone from '../InteractiveZone'

const SLIDE_ID = 'top-offer' as const
const MAX_INCLUSIONS = 3

function TopOfferSlide() {
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const totalsData = useWrappedStorySelector(state => state.totalsData)
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  const isCurrentSlide = useWrappedStorySelector(state => state.currentSlide?.id === SLIDE_ID)

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data

  // Use totals offer when in totals-only mode or user has no top offer
  const userTopOffer = data?.topOffer
  const totalsTopOffer = totalsData?.topOffer
  const useTotalsFallback = isTotalsOnlyMode || !userTopOffer?.imageId
  const topOffer = useTotalsFallback ? totalsTopOffer : userTopOffer
  const visibleInclusions = topOffer?.inclusions.slice(0, MAX_INCLUSIONS) ?? []

  // Adjust wording for totals mode
  const heading = useTotalsFallback
    ? 'The most loved hotel of 2025 was'
    : (topOffer?.isAlternative ? 'Your best-matched hotel is' : 'The hotel you loved the most was')
  const title = useTotalsFallback ? 'Most Loved Offer' : (topOffer?.isAlternative ? 'Top Matched Offer' : 'Top Viewed Offer')

  // Memoize background to prevent recreation
  const background = useMemo(() => {
    if (!topOffer?.imageId) return undefined
    return (
      <WrappedImageBackground
        imageId={topOffer?.imageId}
        alt={topOffer?.name}
        kenBurnsVariant="zoom"
        isActive={isCurrentSlide && !isPaused}
      />
    )
  }, [topOffer?.imageId, topOffer?.name, isCurrentSlide, isPaused])

  // Need a top offer from either user data or totals to render
  if (!topOffer) return null

  return (
    <SlideBase background={background}>
      <FadeBackground>
        <Group direction="vertical" grow verticalAlign="end" horizontalAlign="start" gap={12}>
          <Group direction="vertical" gap={4}>
            <Caption colour="highlight-primary" variant="small" format="uppercase">{title}</Caption>
            <Heading variant="heading5"><b>{heading}</b></Heading>
          </Group>
          <Group direction="vertical" gap={4}>
            <Heading variant="heading1alt">{topOffer?.propertyName}</Heading>
            {!useTotalsFallback && (
              <BodyText variant="medium">
                {topOffer?.location} • Viewed <b>{topOffer?.viewCount} times</b>
              </BodyText>
            )}
            {useTotalsFallback && topOffer?.location && (
              <BodyText variant="medium">
                {topOffer.location}
              </BodyText>
            )}
          </Group>
          <Group direction="vertical" gap={4}>
            {visibleInclusions.length > 0 && <OfferPagePackageInclusionsList>
              {visibleInclusions.map((inclusion) => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} size="small" />)}
            </OfferPagePackageInclusionsList>}
          </Group>
          <InteractiveZone>
            <TextButton
              shape="round"
              fit="full-width"
              kind="secondary"
              variant="default"
              to={`/offer/${topOffer?.id}`}
              target="_blank"
            >
              View offer
            </TextButton>
          </InteractiveZone>
        </Group>
      </FadeBackground>
    </SlideBase>
  )
}

export default React.memo(TopOfferSlide)
